import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { useApiClient } from '../api/ApiClientProvider';
import type { ApiError } from '../api/ApiError';
import { eventKeys } from './queryKeys';
import type { EventDetail, EventSearchParams, EventSummary } from './types';
import { unwrap } from './unwrap';

/**
 * Searches for published events near a location.
 *
 * @param params - The search location, radius, date window and type filter.
 * @param enabled - Whether the search should run; false while a location is still being resolved.
 * @returns The query result for the search.
 */
export function useEventSearch(
  params: EventSearchParams,
  enabled = true,
): UseQueryResult<EventSummary[], ApiError> {
  const client = useApiClient();

  return useQuery<EventSummary[], ApiError>({
    queryKey: eventKeys.search(params),
    enabled,
    queryFn: async () =>
      unwrap(
        await client.GET('/api/events', {
          params: {
            query: {
              lat: params.lat,
              lon: params.lon,
              radiusKm: params.radiusKm,
              from: params.from,
              to: params.to,
              types: params.types === undefined ? undefined : [...params.types],
            },
          },
        }),
        'Could not load events for this area',
      ),
  });
}

/**
 * Loads one event with everything attached to it.
 *
 * @param eventId - The event to load, or an empty string to stay idle.
 * @returns The query result for the event.
 */
export function useEvent(eventId: string): UseQueryResult<EventDetail, ApiError> {
  const client = useApiClient();

  return useQuery<EventDetail, ApiError>({
    queryKey: eventKeys.event(eventId),
    enabled: eventId !== '',
    queryFn: async () =>
      unwrap(
        await client.GET('/api/events/{eventId}', { params: { path: { eventId } } }),
        'Could not load that event',
      ),
  });
}

/**
 * Loads the events the caller has RSVP'd to.
 *
 * @returns The query result for the caller's RSVPs.
 */
export function useMyRsvps(): UseQueryResult<EventSummary[], ApiError> {
  const client = useApiClient();

  return useQuery<EventSummary[], ApiError>({
    queryKey: eventKeys.myRsvps(),
    queryFn: async () =>
      unwrap(await client.GET('/api/me/rsvps'), 'Could not load your RSVPs'),
  });
}

/**
 * Loads the moderation queue. Admin only; a non-admin receives a 403 the caller should not reach.
 *
 * @returns The query result for the queue.
 */
export function useModerationQueue(): UseQueryResult<EventSummary[], ApiError> {
  const client = useApiClient();

  return useQuery<EventSummary[], ApiError>({
    queryKey: eventKeys.moderation(),
    queryFn: async () =>
      unwrap(await client.GET('/api/events/moderation'), 'Could not load the moderation queue'),
  });
}
